import React, { useMemo, useState } from "react";
import {
  COMMON_SCREW_DATA,
  MATERIAL_HP_DATA,
  calculateBoreInterpolation,
  calculateCounterbore,
  calculateCountersink,
  calculateDrillDepth,
  calculateHorsepower,
  calculateKnurl,
  calculateTaper,
  calculateTruePosition,
  formatAngle,
  formatDimension,
  unitLabel,
} from "../../utils/expandedMachiningCalculators";
import {
  TAPER_FAMILIES,
  getTaperReferences,
  resolveTaperGeometry,
} from "../../data/taperReferenceData";

const CALCULATORS = {
  taper: {
    title: "Taper Calculator",
    description: "Select a standard machine taper or calculate custom taper geometry for cutting, setup, and inspection.",
    shopNote: "Lathe compound and taper attachment settings normally use the half angle from the spindle/part axis. Some compounds are graduated from the cross slide, so verify the machine's convention with a light test cut.",
    initial: {
      unit: "inch",
      taperFamily: "morse",
      taperSize: "mt3",
      angleReference: "half",
      largeDiameter: "",
      smallDiameter: "",
      length: "",
      centerDistance: "",
      startZ: "0",
      feedPerRev: "",
    },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "taperFamily", label: "Taper Family", type: "taper-family" },
      { key: "taperSize", label: "Standard Size / Reference", type: "taper-size" },
      { key: "angleReference", label: "Primary Angle Reference", type: "select", options: [["half", "Half Angle / Compound"], ["included", "Included Angle"], ["tpi", "Taper per Inch"], ["tpf", "Taper per Foot"], ["difference", "Diameter Difference over Length"]] },
      { key: "length", label: "Length to Cut / Check", type: "number", step: "0.0001" },
      { key: "largeDiameter", label: "Large Diameter Override", type: "number", step: "0.0001" },
      { key: "smallDiameter", label: "Small Diameter Override", type: "number", step: "0.0001" },
    ],
    calculate: calculateTaper,
    results: (r, p) => [
      ["Diameter Difference", formatDimension(r.difference, p.unit)],
      ["Reference / Standard", r.referenceLabel || "Generic custom taper"],
      ["Reference Length", r.referenceLength > 0 ? formatDimension(r.referenceLength, p.unit) : "Not defined; use the drawing"],
      ["Large Diameter Used", formatDimension(r.largeDiameter, p.unit)],
      ["Small Diameter Used", formatDimension(r.smallDiameter, p.unit)],
      [`Taper Per ${unitLabel(p.unit)}`, `${r.taperPerUnit.toFixed(p.unit === "metric" ? 5 : 6)} ${unitLabel(p.unit)}/${unitLabel(p.unit)}`],
      ["Included Angle", formatAngle(r.includedAngle)],
      ["Half Angle / Compound from Spindle Axis", formatAngle(r.compoundAngle)],
      ["If Compound Reads from Cross Slide", formatAngle(r.compoundFromCrossSlide)],
      ["Taper Attachment Angle", formatAngle(r.taperAttachmentAngle)],
      [p.unit === "metric" ? "Taper per 100 mm" : "Taper per Foot", p.unit === "metric" ? formatDimension(r.taperPer100mm, p.unit) : `${r.taperPerFoot.toFixed(4)} in/ft`],
      ["Tailstock Offset", formatDimension(r.tailstockOffset, p.unit)],
      ["G-code Operation", r.gcodeInputs.operation],
      ["G-code Start X Diameter", formatDimension(r.gcodeInputs.xStartDiameter, p.unit)],
      ["G-code End X Diameter", formatDimension(r.gcodeInputs.xEndDiameter, p.unit)],
      ["G-code Z Start", formatDimension(r.gcodeInputs.zStart, p.unit)],
      ["G-code Z End", formatDimension(r.gcodeInputs.zEnd, p.unit)],
      ["Example Taper Move", r.gcodeInputs.sampleMove],
      ["Reference Source", r.referenceSource || "Standard shop taper geometry"],
    ],
  },
  "drill-depth": {
    title: "Drill Depth Calculator",
    description: "Estimate programmed drill depth required for the full drill diameter to reach a specified depth.",
    shopNote: "This is a geometric drill-point allowance. Real drilling depth can vary with drill grind, spotting method, material, and machine control cycle behavior.",
    initial: { unit: "inch", fullDepth: 1, drillDiameter: 0.25, pointAngle: 118 },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "fullDepth", label: "Final Full-Diameter Hole Depth", type: "number", step: "0.0001" },
      { key: "drillDiameter", label: "Drill Diameter", type: "number", step: "0.0001" },
      { key: "pointAngle", label: "Drill Point Angle", type: "select", options: [["118", "118 deg"], ["135", "135 deg"], ["90", "90 deg"], ["120", "120 deg"]] },
    ],
    calculate: calculateDrillDepth,
    results: (r, p) => [
      ["Drill Tip Allowance", formatDimension(r.tipAllowance, p.unit)],
      ["Total Programmed Drill Depth", formatDimension(r.totalDepth, p.unit)],
    ],
  },
  counterbore: {
    title: "Counterbore Calculator",
    description: "Estimate counterbore diameter and depth from common socket/button/flat head screw reference data or custom fastener measurements.",
    shopNote: "Always verify with the actual fastener and the applicable shop, customer, or company standard before releasing a print or program.",
    initial: { unit: "inch", screwType: "shcs", screwSize: "1/4", customHeadDiameter: "", customHeadHeight: "", clearance: 0.01 },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "screwType", label: "Screw Type", type: "select", options: [["shcs", "Socket Head Cap Screw"], ["button", "Button Head"], ["flat", "Flat Head"], ["custom", "Custom"]] },
      { key: "screwSize", label: "Screw Size", type: "screw-size" },
      { key: "customHeadDiameter", label: "Custom Head Diameter", type: "number", step: "0.0001" },
      { key: "customHeadHeight", label: "Custom Head Height", type: "number", step: "0.0001" },
      { key: "clearance", label: "Desired Clearance", type: "number", step: "0.0001" },
    ],
    calculate: calculateCounterbore,
    results: (r, p) => [
      ["Recommended Counterbore Diameter", formatDimension(r.counterboreDiameter, p.unit)],
      ["Recommended Counterbore Depth", formatDimension(r.counterboreDepth, p.unit)],
      ["Clearance Hole", r.clearanceHole ? formatDimension(r.clearanceHole, p.unit) : "No reference data for selected size"],
      ["Data Source", r.dataSource],
    ],
  },
  countersink: {
    title: "Countersink Calculator",
    description: "Calculate countersink depth from hole diameter, desired top diameter, and included countersink angle.",
    shopNote: "82 degrees is common for inch flat head screws, while 90 degrees is common for metric flat head screws. Measure the actual screw head when fit matters.",
    initial: { unit: "inch", holeDiameter: 0.257, topDiameter: 0.507, angle: 82 },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "holeDiameter", label: "Hole Diameter", type: "number", step: "0.0001" },
      { key: "topDiameter", label: "Desired Top Diameter / Measured Head Diameter", type: "number", step: "0.0001" },
      { key: "angle", label: "Countersink Angle", type: "select", options: [["82", "82 deg"], ["90", "90 deg"], ["100", "100 deg"], ["120", "120 deg"]] },
    ],
    calculate: calculateCountersink,
    results: (r, p) => [
      ["Countersink Depth", formatDimension(r.depth, p.unit)],
      ["Tool Movement Depth", formatDimension(r.toolMovementDepth, p.unit)],
    ],
  },
  "bore-interpolation": {
    title: "Bore Interpolation Calculator",
    description: "Calculate bore interpolation geometry, finish allowance, tolerance status, and cutter comp correction after measuring a bore.",
    shopNote: "This gives centerline geometry and a practical wear-comp correction. Actual bore size still depends on cutter comp mode, machine condition, tool pressure, runout, and finishing strategy.",
    initial: { unit: "inch", boreDiameter: 1, cutterDiameter: 0.5, finishAllowance: 0.005, finishPasses: 1, measuredBore: "", lowerTolerance: 0.999, upperTolerance: 1.001 },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "boreDiameter", label: "Desired Bore Diameter", type: "number", step: "0.0001" },
      { key: "cutterDiameter", label: "Cutter Diameter", type: "number", step: "0.0001" },
      { key: "finishAllowance", label: "Radial Wear / Finish Allowance", type: "number", step: "0.0001" },
      { key: "finishPasses", label: "Finish Pass Count", type: "number", step: "1" },
      { key: "measuredBore", label: "Measured Bore After Cut", type: "number", step: "0.0001" },
      { key: "lowerTolerance", label: "Minimum Allowed Bore", type: "number", step: "0.0001" },
      { key: "upperTolerance", label: "Maximum Allowed Bore", type: "number", step: "0.0001" },
    ],
    calculate: calculateBoreInterpolation,
    results: (r, p) => [
      ["Rough / Finish-Allowance Toolpath Diameter", formatDimension(r.toolpathDiameter, p.unit)],
      ["Rough / Finish-Allowance Toolpath Radius", formatDimension(r.toolpathRadius, p.unit)],
      ["Final Toolpath Diameter", formatDimension(r.finalToolpathDiameter, p.unit)],
      ["Final Toolpath Radius", formatDimension(r.finalToolpathRadius, p.unit)],
      ["Radial Stock Allowance", formatDimension(r.radialStockAllowance, p.unit)],
      ["Stock per Finish Pass", formatDimension(r.finishPassStock, p.unit)],
      ["Measured Diameter Error", formatDimension(r.measuredError, p.unit)],
      ["Radial Cutter Comp Adjustment", formatDimension(r.radialCompAdjustment, p.unit)],
      ["Diameter Program Adjustment", formatDimension(r.diameterCompAdjustment, p.unit)],
      ["Tolerance Band", r.toleranceBand > 0 ? formatDimension(r.toleranceBand, p.unit) : "Enter min/max bore limits"],
      ["Measured Bore Status", r.passFail],
    ],
  },
  knurl: {
    title: "Knurl Calculator",
    description: "Estimate whole-tooth fit around a knurled blank and suggested adjusted blank diameter.",
    shopNote: "Knurling is approximate. Results depend heavily on knurl wheel style, pressure, material flow, blank prep, coolant, and machine rigidity.",
    initial: { unit: "inch", blankDiameter: 0.75, pitch: 32, knurlType: "diamond" },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch / TPI"], ["metric", "Metric / pitch mm"]] },
      { key: "blankDiameter", label: "Blank Diameter", type: "number", step: "0.0001" },
      { key: "pitch", label: "Knurl TPI or Metric Pitch", type: "number", step: "0.0001" },
      { key: "knurlType", label: "Knurl Type", type: "select", options: [["straight", "Approx. Straight"], ["diamond", "Approx. Diamond"]] },
    ],
    calculate: calculateKnurl,
    results: (r, p) => [
      ["Circumference", formatDimension(r.circumference, p.unit)],
      ["Tooth Spacing", formatDimension(r.toothSpacing, p.unit)],
      ["Estimated Tooth Count", r.toothCount.toFixed(2)],
      ["Nearest Whole Tooth Count", r.nearestToothCount],
      ["Suggested Adjusted Blank Diameter", formatDimension(r.adjustedDiameter, p.unit)],
    ],
  },
  "true-position": {
    title: "True Position Calculator",
    description: "Calculate GD&T diametrical true position error, MMC bonus tolerance, datum reference, and coordinate correction.",
    shopNote: "GD&T true position is typically reported as a diametrical tolerance zone from a datum reference frame. This calculator supports common coordinate checks, but does not replace a complete drawing review or CMM inspection plan.",
    initial: { unit: "inch", nominalX: 1, nominalY: 1, actualX: 1.002, actualY: 0.998, tolerance: 0.006, materialCondition: "RFS", featureType: "hole", actualFeatureSize: "", mmcSize: "", datumA: "A", datumB: "B", datumC: "C" },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "nominalX", label: "Nominal X", type: "number", step: "0.0001" },
      { key: "nominalY", label: "Nominal Y", type: "number", step: "0.0001" },
      { key: "actualX", label: "Actual X", type: "number", step: "0.0001" },
      { key: "actualY", label: "Actual Y", type: "number", step: "0.0001" },
      { key: "tolerance", label: "Stated Positional Tolerance", type: "number", step: "0.0001" },
      { key: "materialCondition", label: "Material Condition", type: "select", options: [["RFS", "RFS"], ["MMC", "MMC"], ["LMC", "LMC"]] },
      { key: "featureType", label: "Feature Type", type: "select", options: [["hole", "Hole / Internal Feature"], ["pin", "Pin / External Feature"]] },
      { key: "actualFeatureSize", label: "Actual Feature Size", type: "number", step: "0.0001" },
      { key: "mmcSize", label: "MMC Size", type: "number", step: "0.0001" },
      { key: "datumA", label: "Primary Datum", type: "text" },
      { key: "datumB", label: "Secondary Datum", type: "text" },
      { key: "datumC", label: "Tertiary Datum", type: "text" },
    ],
    calculate: calculateTruePosition,
    results: (r, p) => [
      ["Datum Reference Frame", r.datumReference],
      ["Material Condition", r.materialCondition],
      ["X Deviation", formatDimension(r.xDeviation, p.unit)],
      ["Y Deviation", formatDimension(r.yDeviation, p.unit)],
      ["Radial Error", formatDimension(r.radialError, p.unit)],
      ["True Position Diameter Error", formatDimension(r.truePositionError, p.unit)],
      ["Base Positional Tolerance", formatDimension(r.baseTolerance, p.unit)],
      ["MMC Bonus Tolerance", formatDimension(r.bonusTolerance, p.unit)],
      ["Total Allowed Position", formatDimension(r.totalTolerance, p.unit)],
      ["Remaining Tolerance", formatDimension(r.remainingTolerance, p.unit)],
      ["Tolerance Used", r.totalTolerance > 0 ? `${r.toleranceUsed.toFixed(1)}%` : "No tolerance entered"],
      ["Move Correction in X", formatDimension(r.correctionX, p.unit)],
      ["Move Correction in Y", formatDimension(r.correctionY, p.unit)],
      ["Pass / Fail", r.passFail],
    ],
  },
  horsepower: {
    title: "Horsepower Calculator",
    description: "Estimate cutting horsepower from material removal rate and unit horsepower values.",
    shopNote: "Horsepower is an estimate. Tool geometry, coating, material hardness, coolant, machine condition, and engagement all affect actual power draw.",
    initial: { unit: "inch", material: "mild-steel", materialRemovalRate: "", widthOfCut: 0.5, depthOfCut: 0.1, feedrate: 20, unitHorsepower: "", efficiency: 80 },
    fields: [
      { key: "unit", label: "Unit", type: "select", options: [["inch", "Inch"], ["metric", "Metric"]] },
      { key: "material", label: "Material", type: "material-hp" },
      { key: "materialRemovalRate", label: "MRR Direct Entry (in^3/min or mm^3/min)", type: "number", step: "0.0001" },
      { key: "widthOfCut", label: "Width of Cut", type: "number", step: "0.0001" },
      { key: "depthOfCut", label: "Depth of Cut", type: "number", step: "0.0001" },
      { key: "feedrate", label: "Feedrate", type: "number", step: "0.0001" },
      { key: "unitHorsepower", label: "Unit HP (optional)", type: "number", step: "0.01" },
      { key: "efficiency", label: "Machine Efficiency %", type: "number", step: "1" },
    ],
    calculate: calculateHorsepower,
    results: (r, p) => [
      ["Material", r.materialLabel],
      ["MRR", p.unit === "metric" ? `${r.mrrDisplay.toFixed(3)} mm^3/min` : `${r.mrrIn3Min.toFixed(3)} in^3/min`],
      ["Unit Horsepower", `${r.unitHp.toFixed(2)} HP per in^3/min`],
      ["Estimated Cutting HP", `${r.cuttingHp.toFixed(2)} HP`],
      ["Estimated Spindle HP Required", `${r.spindleHp.toFixed(2)} HP`],
    ],
  },
};

const ExpandedShopCalculator = ({ toolId, onSave }) => {
  const config = CALCULATORS[toolId] || CALCULATORS.taper;
  const [params, setParams] = useState(config.initial);
  const [touched, setTouched] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);

  const taperGeometry = useMemo(
    () => (toolId === "taper" ? resolveTaperGeometry(params) : null),
    [params, toolId]
  );
  const calculationParams = useMemo(() => {
    if (toolId !== "taper") return params;
    return {
      ...params,
      largeDiameter: taperGeometry.largeDiameter,
      smallDiameter: taperGeometry.smallDiameter,
      length: taperGeometry.length,
      centerDistance: Number(params.centerDistance) > 0 ? params.centerDistance : taperGeometry.length,
    };
  }, [params, taperGeometry, toolId]);
  const result = useMemo(() => {
    const calculated = config.calculate(calculationParams);
    if (toolId !== "taper" || calculated.errors) return calculated;
    return {
      ...calculated,
      largeDiameter: taperGeometry.largeDiameter,
      smallDiameter: taperGeometry.smallDiameter,
      referenceLength: taperGeometry.referenceLength,
      referenceLabel: taperGeometry.reference ? `${TAPER_FAMILIES.find((item) => item.id === taperGeometry.reference.family)?.label || taperGeometry.reference.family} ${taperGeometry.reference.size}` : "",
      referenceSource: taperGeometry.reference?.source || "",
      referenceNotes: taperGeometry.reference?.notes || "",
    };
  }, [calculationParams, config, taperGeometry, toolId]);
  const hasErrors = result?.errors?.length > 0;

  const updateField = (key, value) => {
    setTouched(true);
    setParams((current) => {
      const next = { ...current, [key]: value };
      if (toolId === "taper" && key === "taperFamily") {
        next.taperSize = getTaperReferences(value)[0]?.id || "";
        next.largeDiameter = "";
        next.smallDiameter = "";
        next.length = "";
      }
      if (toolId === "taper" && key === "taperSize") {
        next.largeDiameter = "";
        next.smallDiameter = "";
        next.length = "";
      }
      if (toolId === "taper" && key === "unit" && current.unit !== value) {
        const factor = value === "metric" ? 25.4 : 1 / 25.4;
        ["largeDiameter", "smallDiameter", "length", "centerDistance", "startZ"].forEach((field) => {
          if (String(current[field] ?? "").trim() !== "") next[field] = String(Number(current[field]) * factor);
        });
      }
      if (key === "unit" && current.unit !== value && config.fields.some((field) => field.type === "screw-size")) {
        next.screwSize = COMMON_SCREW_DATA[value]?.[0]?.size || "";
        next.clearance = value === "metric" ? 0.25 : 0.01;
      }
      return next;
    });
  };

  const reset = () => {
    setParams(config.initial);
    setTouched(false);
    setAdvancedMode(false);
  };

  const handleSave = () => {
    if (hasErrors) return;
    onSave?.({
      summary: `${config.title} - ${config.results(result, params)[0]?.[1] || "calculation"}`,
      input: params,
      results: result,
      formulas: result.formulas || [],
    });
  };

  const renderField = (field) => {
    if (field.type === "taper-family") {
      return (
        <select value={params.taperFamily} onChange={(event) => updateField(field.key, event.target.value)}>
          {TAPER_FAMILIES.map((family) => <option key={family.id} value={family.id}>{family.label}</option>)}
        </select>
      );
    }

    if (field.type === "taper-size") {
      const references = getTaperReferences(params.taperFamily);
      return (
        <select value={params.taperSize} onChange={(event) => updateField(field.key, event.target.value)} disabled={params.taperFamily === "custom"}>
          {params.taperFamily === "custom" && <option value="">Custom dimensions</option>}
          {references.map((entry) => <option key={entry.id} value={entry.id}>{entry.size}</option>)}
        </select>
      );
    }
    if (field.type === "select") {
      return (
        <select value={params[field.key]} onChange={(event) => updateField(field.key, event.target.value)}>
          {field.options.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      );
    }

    if (field.type === "screw-size") {
      return (
        <select value={params.screwSize} onChange={(event) => updateField("screwSize", event.target.value)}>
          {(COMMON_SCREW_DATA[params.unit] || []).map((item) => (
            <option key={item.size} value={item.size}>{item.size}</option>
          ))}
        </select>
      );
    }

    if (field.type === "material-hp") {
      return (
        <select value={params.material} onChange={(event) => updateField("material", event.target.value)}>
          {MATERIAL_HP_DATA.map((item) => (
            <option key={item.id} value={item.id}>{item.name} ({item.unitHp.toFixed(2)} unit HP)</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type}
        step={field.step}
        value={params[field.key]}
        onChange={(event) => updateField(field.key, event.target.value)}
      />
    );
  };

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header">
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <h3>Inputs</h3>
            {toolId === "taper" && (
              <div className="taper-mode-bar">
                <div>
                  <strong>{advancedMode ? "Advanced setup mode" : "Standard taper mode"}</strong>
                  <span>{advancedMode ? "Adds machine setup, G-code, and inspection controls." : "Choose a reference and enter the length to cut or inspect."}</span>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => setAdvancedMode((current) => !current)}>
                  {advancedMode ? "Use Standard Mode" : "Open Advanced Mode"}
                </button>
              </div>
            )}
            <div className="calculator-input-grid">
              {config.fields.map((field) => (
                <label key={field.key} className="form-group">
                  {field.label}
                  {renderField(field)}
                </label>
              ))}
              {toolId === "taper" && advancedMode && [
                { key: "centerDistance", label: "Distance Between Centers", type: "number", step: "0.0001" },
                { key: "startZ", label: "Lathe Z Start for G-code", type: "number", step: "0.0001" },
                { key: "feedPerRev", label: "Optional Feed per Rev for G-code", type: "number", step: "0.0001" },
              ].map((field) => (
                <label key={field.key} className="form-group">
                  {field.label}
                  {renderField(field)}
                </label>
              ))}
            </div>
            {toolId === "taper" && taperGeometry.reference && (
              <div className="taper-reference-note">
                <strong>{taperGeometry.reference.source}</strong>
                <p>{taperGeometry.reference.notes}</p>
              </div>
            )}

            <div className="calculator-actions">
              <button type="button" className="btn btn-secondary" onClick={reset}>Reset</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={hasErrors}>Save Calculation</button>
            </div>
          </div>

          <div className="calculator-panel">
            <h3>Results</h3>
            {hasErrors ? (
              <div className="warnings">
                <h4>Check Inputs</h4>
                <ul>
                  {result.errors.map((error) => <li key={error}>{error}</li>)}
                </ul>
              </div>
            ) : (
              <>
                <div className="calculator-results-grid">
                  {config.results(result, params).map(([label, value]) => (
                    <div key={label} className="calculator-result-card">
                      <span className="calculator-result-label">{label}</span>
                      <strong className="calculator-result-value">{value}</strong>
                    </div>
                  ))}
                </div>

                {result.warnings?.length > 0 && (
                  <div className="warnings">
                    <h4>Warnings</h4>
                    <ul>
                      {result.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                    </ul>
                  </div>
                )}
                {toolId === "taper" && !hasErrors && (
                  <div className="taper-guidance-grid">
                    <section>
                      <h4>Setup Guidance</h4>
                      <p>Set a conventional lathe compound or taper attachment to the half angle shown above, measured from the spindle axis. If the machine scale reads from the cross slide, use the complementary setting shown.</p>
                    </section>
                    <section>
                      <h4>Inspection Guidance</h4>
                      <p>Use a certified mating gauge, taper ring/plug gauge, or controlled diameter-over-length check. Blueing contact alone can show bearing but does not establish size or full geometric conformance.</p>
                    </section>
                  </div>
                )}
              </>
            )}

            <section style={{ marginTop: "18px" }}>
              <h4>Formula Used</h4>
              <ul style={{ color: "var(--text-secondary)", paddingLeft: "18px" }}>
                {(result.formulas || []).map((formula) => <li key={formula}>{formula}</li>)}
              </ul>
            </section>

            <section className="calculator-result-card" style={{ marginTop: "18px" }}>
              <span className="calculator-result-label">Shop Note</span>
              <p style={{ margin: 0 }}>{config.shopNote}</p>
              {touched && hasErrors && <p style={{ marginTop: "8px" }}>Correct the highlighted input issue before using this result.</p>}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EXPANDED_SHOP_CALCULATOR_LABELS = Object.fromEntries(
  Object.entries(CALCULATORS).map(([id, config]) => [id, config.title])
);

export default ExpandedShopCalculator;
